/**
 * NHL RTDB Configuration
 * Centralized configuration for NHL Firebase Realtime Database caching
 * Adapted from NFL implementation with hockey-specific modifications
 */

// Firebase RTDB instance for NHL (separate from NFL/football)
export const NHL_RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-2d6f5.firebaseio.com/';

// NHL API Configuration
export const NHL_API_CONFIG = {
  LEAGUE_ID: 57, // NHL league ID in api-sports.io (confirmed: USA NHL, 2025 season)
  LEAGUE_ID_4_NATIONS: 271, // NHL 4 Nations Face-Off (Feb 2025)
  DEFAULT_TIMEZONE: 'America/New_York' // Eastern Time
};

// Cache paths structure
export const CACHE_PATHS = {
  // Root path for all NHL cache
  ROOT: 'cache/nhl',

  // Games caching paths
  GAMES: 'cache/nhl/games',
  GAMES_DATES: 'cache/nhl/games/dates',
  GAMES_SEASONS: 'cache/nhl/games/seasons',
  GAMES_LIVE: 'cache/nhl/games/live',

  // Other data types
  STANDINGS: 'cache/nhl/standings',
  PLAYERS: 'cache/nhl/players',
  TEAMS: 'cache/nhl/teams',
  ODDS: 'cache/nhl/odds',

  // Metadata
  STATS: 'cache/nhl/metadata/stats',
  POPULAR: 'cache/nhl/metadata/popular'
};

// TTL configurations (in milliseconds)
export const TTL_CONFIG = {
  // Live data - very short TTL
  LIVE_GAMES: 30 * 1000,           // 30 seconds
  LIVE_ODDS: 60 * 1000,             // 1 minute

  // Game data - moderate TTL
  TODAY_GAMES: 5 * 60 * 1000,       // 5 minutes
  DATE_GAMES: 5 * 60 * 1000,        // 5 minutes
  UPCOMING_GAMES: 10 * 60 * 1000,   // 10 minutes

  // Completed games - longer TTL
  PAST_GAMES: 60 * 60 * 1000,       // 1 hour
  HISTORICAL_GAMES: 24 * 60 * 60 * 1000, // 24 hours

  // Standings and stats - longer TTL
  STANDINGS: 6 * 60 * 60 * 1000,    // 6 hours
  DIVISION_STANDINGS: 6 * 60 * 60 * 1000, // 6 hours

  // Static data - very long TTL
  TEAMS: 24 * 60 * 60 * 1000,       // 24 hours
  PLAYERS: 12 * 60 * 60 * 1000,     // 12 hours
  PLAYER_STATS: 6 * 60 * 60 * 1000, // 6 hours

  // Odds data
  PREMATCH_ODDS: 5 * 60 * 1000,     // 5 minutes

  // Default fallback
  DEFAULT: 5 * 60 * 1000            // 5 minutes
};

// Season mappings for NHL
// NHL seasons run from October to June (e.g., 2025-26 season)
// Keys use 4-digit format to match NHL API (e.g., 2025 for 2025-26 season)
export const SEASON_MAPPINGS = {
  // 2024-25 Season (previous season) - API key: 2024
  2024: {
    regularSeason: {
      start: '2024-10-08',
      end: '2025-04-17',
      name: '2024-25 Regular Season'
    },
    playoffs: {
      firstRound: {
        start: '2025-04-20',
        end: '2025-05-04',
        name: 'First Round'
      },
      secondRound: {
        start: '2025-05-05',
        end: '2025-05-22',
        name: 'Conference Semifinals'
      },
      conferenceFinals: {
        start: '2025-05-23',
        end: '2025-06-08',
        name: 'Conference Finals'
      },
      stanleyCup: {
        start: '2025-06-09',
        end: '2025-06-30',
        name: 'Stanley Cup Finals'
      }
    }
  },

  // 2025-26 Season (CURRENT SEASON) - API key: 2025
  2025: {
    regularSeason: {
      start: '2025-10-07',
      end: '2026-04-16',
      name: '2025-26 Regular Season'
    },
    playoffs: {
      firstRound: {
        start: '2026-04-19',
        end: '2026-05-03',
        name: 'First Round'
      },
      secondRound: {
        start: '2026-05-04',
        end: '2026-05-21',
        name: 'Conference Semifinals'
      },
      conferenceFinals: {
        start: '2026-05-22',
        end: '2026-06-07',
        name: 'Conference Finals'
      },
      stanleyCup: {
        start: '2026-06-08',
        end: '2026-06-29',
        name: 'Stanley Cup Finals'
      }
    }
  },

  // 2026-27 Season (future season) - API key: 2026
  2026: {
    regularSeason: {
      start: '2026-10-06',
      end: '2027-04-15',
      name: '2026-27 Regular Season'
    },
    playoffs: {
      firstRound: {
        start: '2027-04-18',
        end: '2027-05-02',
        name: 'First Round'
      },
      secondRound: {
        start: '2027-05-03',
        end: '2027-05-20',
        name: 'Conference Semifinals'
      },
      conferenceFinals: {
        start: '2027-05-21',
        end: '2027-06-06',
        name: 'Conference Finals'
      },
      stanleyCup: {
        start: '2027-06-07',
        end: '2027-06-28',
        name: 'Stanley Cup Finals'
      }
    }
  }
};

// Cache key generation helpers
export const getCacheKey = (type, ...params) => {
  // Sanitize parameters to create valid Firebase keys
  const sanitized = params
    .filter(p => p !== null && p !== undefined)
    .map(p => String(p).replace(/[.#$\[\]]/g, '_'))
    .join('_');

  return sanitized;
};

// NHL Game Status Codes
export const GAME_STATUS = {
  // Scheduled
  NOT_STARTED: 'NS',

  // Live (In Play)
  PERIOD_1: 'P1',
  PERIOD_2: 'P2',
  PERIOD_3: 'P3',
  OVERTIME: 'OT',
  PENALTIES: 'PT',
  BREAK_TIME: 'BT',

  // Final
  FINISHED: 'FT',
  AFTER_OVERTIME: 'AOT',
  AFTER_PENALTIES: 'AP',
  AWARDED: 'AW',

  // Other
  POSTPONED: 'POST',
  CANCELLED: 'CANC',
  INTERRUPTED: 'INTR',
  ABANDONED: 'ABD'
};

// Live game statuses
export const LIVE_STATUSES = [
  GAME_STATUS.PERIOD_1,
  GAME_STATUS.PERIOD_2,
  GAME_STATUS.PERIOD_3,
  GAME_STATUS.OVERTIME,
  GAME_STATUS.PENALTIES,
  GAME_STATUS.BREAK_TIME
];

// Final game statuses
export const FINAL_STATUSES = [
  GAME_STATUS.FINISHED,
  GAME_STATUS.AFTER_OVERTIME,
  GAME_STATUS.AFTER_PENALTIES,
  GAME_STATUS.AWARDED
];

// Check if game is live
export const isGameLive = (status) => {
  return LIVE_STATUSES.includes(status);
};

// Check if game is final
export const isGameFinal = (status) => {
  return FINAL_STATUSES.includes(status);
};

// Get game state category
export const getGameState = (status) => {
  if (LIVE_STATUSES.includes(status)) return 'LIVE';
  if (FINAL_STATUSES.includes(status)) return 'FINAL';
  if (status === GAME_STATUS.NOT_STARTED) return 'SCHEDULED';
  return 'OTHER';
};

// Format period/status for display
export const formatGameStatus = (status) => {
  const statusMap = {
    [GAME_STATUS.NOT_STARTED]: 'Scheduled',
    [GAME_STATUS.PERIOD_1]: '1st Period',
    [GAME_STATUS.PERIOD_2]: '2nd Period',
    [GAME_STATUS.PERIOD_3]: '3rd Period',
    [GAME_STATUS.OVERTIME]: 'Overtime',
    [GAME_STATUS.PENALTIES]: 'Shootout',
    [GAME_STATUS.BREAK_TIME]: 'Intermission',
    [GAME_STATUS.FINISHED]: 'Final',
    [GAME_STATUS.AFTER_OVERTIME]: 'Final/OT',
    [GAME_STATUS.AFTER_PENALTIES]: 'Final/SO',
    [GAME_STATUS.AWARDED]: 'Final (Awarded)',
    [GAME_STATUS.POSTPONED]: 'Postponed',
    [GAME_STATUS.CANCELLED]: 'Cancelled',
    [GAME_STATUS.INTERRUPTED]: 'Interrupted',
    [GAME_STATUS.ABANDONED]: 'Abandoned'
  };
  return statusMap[status] || status;
};

// Determine TTL based on game status
export const getTTLForGame = (game) => {
  const status = game?.status?.short || '';

  // Live game statuses
  if (isGameLive(status)) {
    return TTL_CONFIG.LIVE_GAMES;
  }

  // Completed game statuses
  if (isGameFinal(status)) {
    // Check if game is from today
    const gameDate = game?.date?.split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    if (gameDate === today) {
      return TTL_CONFIG.PAST_GAMES;
    }
    return TTL_CONFIG.HISTORICAL_GAMES;
  }

  // Upcoming games
  return TTL_CONFIG.UPCOMING_GAMES;
};

// Debug flag
export const DEBUG_CACHE = process.env.NODE_ENV === 'development';

// Logging helper
export const logCache = (level, message, data = null) => {
  if (DEBUG_CACHE) {
    const prefix = '🏒 [NHL-RTDB]';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];

    switch (level) {
      case 'hit':
        console.log(`${prefix} ✅ ${timestamp} - ${message}`, data || '');
        break;
      case 'miss':
        console.log(`${prefix} ❌ ${timestamp} - ${message}`, data || '');
        break;
      case 'set':
        console.log(`${prefix} 💾 ${timestamp} - ${message}`, data || '');
        break;
      case 'error':
        console.error(`${prefix} 🚨 ${timestamp} - ${message}`, data || '');
        break;
      case 'info':
      default:
        console.log(`${prefix} ℹ️ ${timestamp} - ${message}`, data || '');
        break;
    }
  }
};

// Helper to determine if we should use cache based on data freshness
export const shouldUseCache = (cacheData) => {
  if (!cacheData) return false;

  const { ttl, lastUpdated } = cacheData;
  const now = Date.now();
  const age = now - lastUpdated;

  return age < ttl;
};

// Popular teams for pre-warming cache
export const POPULAR_TEAMS = [
  { id: 6, name: 'Boston Bruins' },
  { id: 10, name: 'Toronto Maple Leafs' },
  { id: 3, name: 'New York Rangers' },
  { id: 12, name: 'Carolina Hurricanes' },
  { id: 16, name: 'Chicago Blackhawks' },
  { id: 26, name: 'Tampa Bay Lightning' },
  { id: 14, name: 'Colorado Avalanche' },
  { id: 28, name: 'Vegas Golden Knights' }
];

// Get current NHL season
export const getCurrentSeason = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // NHL season runs Oct-Jun
  // If Jan-Sep, we're in season that started last year
  // If Oct-Dec, we're in season that starts this year
  // NHL API uses 4-digit year format (e.g., 2025 for 2025-26 season)
  return currentMonth >= 10 ? currentYear : currentYear - 1;
};

// Get current season stage
export const getCurrentSeasonStage = () => {
  const now = new Date();
  const currentDate = now.toISOString().split('T')[0];
  const season = getCurrentSeason();

  const seasonData = SEASON_MAPPINGS[season];
  if (!seasonData) {
    return { stage: 'regular', name: 'Regular Season', season };
  }

  // Check regular season
  if (currentDate >= seasonData.regularSeason.start && currentDate <= seasonData.regularSeason.end) {
    return {
      stage: 'regular',
      name: seasonData.regularSeason.name,
      season
    };
  }

  // Check playoffs
  for (const [roundKey, roundData] of Object.entries(seasonData.playoffs)) {
    if (currentDate >= roundData.start && currentDate <= roundData.end) {
      return {
        stage: 'playoffs',
        round: roundKey,
        name: roundData.name,
        season
      };
    }
  }

  // Off-season
  return {
    stage: 'offseason',
    name: 'Off-season',
    season
  };
};

// Export all configurations
export default {
  NHL_RTDB_URL,
  CACHE_PATHS,
  TTL_CONFIG,
  SEASON_MAPPINGS,
  GAME_STATUS,
  LIVE_STATUSES,
  FINAL_STATUSES,
  getCacheKey,
  getTTLForGame,
  isGameLive,
  isGameFinal,
  getGameState,
  formatGameStatus,
  DEBUG_CACHE,
  logCache,
  shouldUseCache,
  POPULAR_TEAMS,
  getCurrentSeason,
  getCurrentSeasonStage
};
